// Beslenme öyküsü (2026-10-05) — diyetisyenin ilk görüşmedeki anamnezi.
// Sunucu sürümlü tutar: aynı kişinin 24 saat içindeki kaydı aynı sürümü günceller,
// sonrası yeni sürüm açar. Sol: sürümler, sağ: form (yalnız son sürüm düzenlenir).

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { MenuBook as IntakeIcon } from "@mui/icons-material";

import SelectableListRow from "../../molecules/SelectableListRow";
import ActionButton from "../../atoms/ActionButton";
import FormTextField from "../../atoms/FormTextField";
import ChoiceChipGroup from "../../atoms/ChoiceChipGroup";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  ListGroupLabel,
  ReadOnlyStrip,
} from "./_workspaceParts";
import { SECTION_SX, FieldRow, PdfButton } from "./_editorParts";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useAlert } from "../../../context/AlertProvider";
import {
  useListNutritionIntakesMutation,
  useSaveNutritionIntakeMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";
import { INTAKE_ACTIVITY as ACTIVITY, INTAKE_FIELDS as FIELDS } from "../DietPlan/nutritionIntakeFormat";

const empty = () => Object.fromEntries([...FIELDS.map((f) => [f.key, ""]), ["activityLevel", ""]]);
const fromRecord = (r) => ({ ...empty(), ...Object.fromEntries(Object.keys(empty()).map((k) => [k, r?.[k] || ""])) });

// @react-pdf + fontlar ayrı chunk (bkz. TreatmentEditor)
const NutritionIntakePreview = lazy(() => import("../pdf/NutritionIntakePreview"));

const formatDate = (v) =>
  v ? new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const authorOf = (r) => (r?.createdBy ? `${r.createdBy.name || ""} ${r.createdBy.surname || ""}`.trim() : "");

export default function NutritionIntakeTab({ patientId, patient, token, canEdit = true, examinationId }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const canSave = useHasPermission("diet:update") && canEdit;
  const [listIntakes, { isLoading }] = useListNutritionIntakesMutation();
  const [saveIntake, { isLoading: saving }] = useSaveNutritionIntakeMutation();

  const [intakes, setIntakes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(empty);
  const [creatingNew, setCreatingNew] = useState(false);
  const [printing, setPrinting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await listIntakes({ token, patientId }).unwrap();
      const list = res?.data?.intakes || [];
      setIntakes(list);
      setSelectedId((cur) => (cur && list.some((i) => i._id === cur) ? cur : list[0]?._id || null));
    } catch (_) {
      showError("Beslenme öyküsü yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [listIntakes, token, patientId, showError]);

  useEffect(() => {
    if (token && patientId) refresh();
  }, [token, patientId, refresh]);

  const selected = useMemo(() => intakes.find((i) => i._id === selectedId) || null, [intakes, selectedId]);
  const isLatest = selected && intakes[0]?._id === selected._id;
  const editable = canSave && (creatingNew || isLatest || intakes.length === 0);

  useEffect(() => {
    if (!creatingNew) setForm(fromRecord(selected));
  }, [selected, creatingNew]);

  const handleSave = async () => {
    try {
      const res = await saveIntake({ token, patientId, ...(examinationId ? { examinationId } : {}), ...form }).unwrap();
      setCreatingNew(false);
      await refresh();
      if (res?.data?.intake?._id) setSelectedId(res.data.intake._id);
      showSuccess("Beslenme öyküsü kaydedildi.");
    } catch (err) {
      showError(err?.data?.message || "Kaydedilemedi.");
    }
  };

  const formView = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {!editable && <ReadOnlyStrip>Önceki kayıt — yalnızca görüntüleme</ReadOnlyStrip>}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ ...SECTION_SX, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <FieldRow label="Aktivite düzeyi">
            <ChoiceChipGroup
              options={ACTIVITY}
              value={form.activityLevel || null}
              onChange={(v) => setForm((f) => ({ ...f, activityLevel: v }))}
              disabled={!editable}
              palette={palette}
              ariaLabel="Aktivite düzeyi"
            />
          </FieldRow>
          {FIELDS.map((f) => (
            <FieldRow key={f.key} label={f.label}>
              <FormTextField
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                multiline
                minRows={1}
                placeholder={editable ? f.placeholder : ""}
                value={form[f.key] || ""}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                disabled={!editable}
              />
            </FieldRow>
          ))}
        </Box>
      </Box>
      {editable && (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 1.5, borderTop: `1px solid ${COLORS.slate100}`, display: "flex", justifyContent: "flex-end" }}>
          <ActionButton variant="save" palette={palette} loading={saving} onClick={handleSave} />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Beslenme Öyküsü"
        count={intakes.length}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {selected && !creatingNew ? (
              <PdfButton onClick={() => setPrinting(true)} title="Beslenme öyküsü çıktısı (PDF)" palette={palette} />
            ) : null}
            {canSave && intakes.length > 0 && !creatingNew ? (
              <ActionButton
                variant="add"
                compact
                palette={palette}
                label="Yeni kayıt"
                onClick={() => {
                  setCreatingNew(true);
                  setForm(fromRecord(intakes[0]));
                }}
              />
            ) : null}
          </Box>
        }
      >
        {!loaded && isLoading ? (
          <WorkspaceSkeleton />
        ) : intakes.length === 0 && !creatingNew ? (
          <WorkspaceEmpty
            icon={<IntakeIcon />}
            title="Henüz beslenme öyküsü yok"
            lead="İlk görüşmede"
            linkLabel="beslenme öyküsü alın"
            readOnlyText={`Bu ${terms.patientLower} için beslenme öyküsü kaydı bulunmuyor.`}
            onCreate={canSave ? () => setCreatingNew(true) : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <>
                <ListGroupLabel first count={intakes.length}>
                  Kayıtlar
                </ListGroupLabel>
                <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                  {creatingNew && (
                    <SelectableListRow active sx={LIST_ROW_SX} title="Yeni kayıt" meta="Kaydedilmedi" />
                  )}
                  {intakes.map((i, idx) => (
                    <SelectableListRow
                      key={i._id}
                      active={!creatingNew && i._id === selectedId}
                      onClick={() => {
                        setCreatingNew(false);
                        setSelectedId(i._id);
                      }}
                      sx={LIST_ROW_SX}
                      title={idx === 0 ? "Güncel öykü" : "Önceki kayıt"}
                      meta={`${formatDate(i.updatedAt || i.createdAt)}${authorOf(i) ? ` · ${authorOf(i)}` : ""}`}
                    />
                  ))}
                </Stack>
                {creatingNew && (
                  <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, px: { xs: 2, md: 4 }, py: 1 }}>
                    Önceki kayıttaki bilgiler kopyalandı; değişenleri güncelleyip kaydedin.
                  </Typography>
                )}
              </>
            }
            editor={formView}
          />
        )}
      </WorkspaceBody>
      {printing && (
        <Suspense fallback={null}>
          <NutritionIntakePreview
            open={printing}
            onClose={() => setPrinting(false)}
            patient={patient}
            intake={{ ...selected, ...form }}
          />
        </Suspense>
      )}
    </Box>
  );
}
